import { Meeting } from '../models/meeting';
import { ApiEvent } from '../services/event.service';

/** A meeting resolved to the concrete span a calendar grid can draw. */
export interface ScheduledMeeting {
  meeting: Meeting;
  start: Date;
  end: Date;
}

/** Formats a Date's calendar day as the "YYYY-MM-DD" an event carries. */
function toDayString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Formats a Date's clock time as the "HH:MM:SS" an event carries. */
function toTimeString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
}

/**
 * A confirmed meeting shaped as the event a calendar already knows how to draw,
 * so the grid needs no second code path for meetings. Only the tile's color
 * and where a click goes tell them apart.
 *
 * The title carries both facts a tile has room for: what the meeting is about,
 * and who it's with. Quoting the subject keeps the two readable when they run
 * together at 9px.
 * @param scheduled - a meeting and the span it occupies
 * @param withName - the other participant, from the caller's point of view
 * @returns {ApiEvent}
 */
export function meetingAsEvent({ meeting, start, end }: ScheduledMeeting, withName: string) {
  const subject = meeting.title ? `"${meeting.title}"` : 'Meeting';

  return {
    id: meeting.id,
    title: withName ? `${subject} with ${withName}` : subject,
    day: toDayString(start),
    start_time: toTimeString(start),
    end_time: toTimeString(end),
    // A join link is far too long to read on a tile, so a remote meeting says
    // only that it is one and leaves the link to the meetings page.
    place: meeting.meetingType === 'remote' ? 'Remote' : (meeting.location ?? undefined),
    description: meeting.description ?? undefined,
    is_shared: false,
    user_id: meeting.facultyUserId,
  } satisfies ApiEvent;
}

/**
 * The meetings that belong on a calendar grid, each paired with the span it
 * occupies.
 *
 * Confirmed only. A past confirmed one stays in — navigating back should show
 * what actually happened — while canceled and declined ones drop out. Pending
 * is deliberately excluded.
 *
 * Calendars only ever read meetings through this — nothing is mirrored into
 * the events table, so a reschedule or cancellation reaches the grid on the
 * next load with no sync step that could fall behind.
 * @param meetings - every meeting the current user participates in
 * @returns {ScheduledMeeting[]} in the order given, minus what can't be drawn
 */
export function scheduledMeetings(meetings: Meeting[]): ScheduledMeeting[] {
  const scheduled: ScheduledMeeting[] = [];

  for (const meeting of meetings) {
    if (meeting.status !== 'confirmed') continue;

    const start = meeting.start;
    let end = meeting.end;
    // start and end are null together, both being built off the same start
    // time. A row carrying no duration still resolves to a zero-length span,
    // which the grid would draw as a negative height, so it goes too.
    if (!start || !end || end <= start) continue;

    // A slot running past midnight ends on the following day, which a grid of
    // one column per day would likewise draw as a negative height. Cap it at
    // the end of the day it starts on; the details dialog still reports the
    // real end.
    if (end.toDateString() !== start.toDateString()) {
      end = new Date(start);
      end.setHours(23, 59, 0, 0);
    }

    scheduled.push({ meeting, start, end });
  }

  return scheduled;
}
